import { Button as IconButton } from '@/components/ui/button'
import { centsToEuros } from '@/constants'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import type { CatalogueItemData, InventoryItemData, InventorySplitRequest } from '@jobilla/nexus-sdk'
import type { AxiosError } from 'axios'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountField, Button } from '../../../../rapid-ui'
import { DescriptionCombobox } from './DescriptionCombobox'

interface SplitInventoryDialogProps {
	open: boolean
	item: InventoryItemData | null
	onClose: () => void
	onConfirm: (args: { currentQuantity: number; splits: InventorySplitRequest['splits'] }) => void
	catalogueItems: CatalogueItemData[]
	isLoading: boolean
	error: AxiosError<{ message?: string }> | null
}

interface DraftRow {
	key: string
	quantity: number
	valueCents: number
	description: string
}

function newRowKey(): string {
	return `row-${Math.random().toString(36).slice(2, 10)}`
}

/** A partly held item starts with its available units split off, so the holds stay on the original. */
function defaultRows(item: InventoryItemData): DraftRow[] {
	const isPartlyHeld = item.availableQuantity > 0 && item.availableQuantity < item.quantity

	if (!isPartlyHeld) {
		return []
	}

	return [{
		key: newRowKey(),
		quantity: item.availableQuantity,
		valueCents: item.valueCents,
		description: item.description ?? '',
	}]
}

function clamp(value: number, min: number): number {
	if (Number.isNaN(value)) { return min }
	return value < min ? min : value
}

function eurosToCents(value: number): number {
	if (Number.isNaN(value)) { return 0 }
	return Math.max(0, Math.round(value * 100))
}

export function SplitInventoryDialog({
	open,
	item,
	onClose,
	onConfirm,
	catalogueItems,
	isLoading,
	error,
}: SplitInventoryDialogProps) {
	const { t, i18n } = useTranslation('common')
	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
	})
	const formatCents = (cents: number) => currencyFormatter.format(centsToEuros(cents))
	const [rows, setRows] = useState<DraftRow[]>(() => (item ? defaultRows(item) : []))

	useEffect(() => {
		if (item) {
			setRows(defaultRows(item))
		}
	}, [item])

	if (!item) { return null }

	const total = item.quantity
	const unitPrice = item.valueCents
	const originalTotalValue = total * unitPrice
	const newRowsValue = rows.reduce((sum, row) => sum + (row.quantity * row.valueCents), 0)
	const remainingValue = originalTotalValue - newRowsValue

	let computedCurrentQuantity = 0
	let balanceCents = 0
	let isOver = false
	let isCurrentEmpty = false

	if (remainingValue < 0) {
		isOver = true
	} else if (unitPrice > 0) {
		if (remainingValue < unitPrice) {
			isCurrentEmpty = true
		} else {
			computedCurrentQuantity = Math.floor(remainingValue / unitPrice)
			balanceCents = remainingValue - (computedCurrentQuantity * unitPrice)
		}
	} else {
		const newRowsQuantity = rows.reduce((sum, row) => sum + row.quantity, 0)
		const remainingQuantity = total - newRowsQuantity
		if (remainingQuantity < 1) {
			isCurrentEmpty = true
		} else {
			computedCurrentQuantity = remainingQuantity
		}
	}

	const heldQuantity = item.quantity - item.availableQuantity
	const isBelowHeld = !isOver && !isCurrentEmpty && computedCurrentQuantity < heldQuantity
	const hasAutoBalance = balanceCents > 0
	const canSplit = total >= 2
	const allRowsValid = rows.every((row) => row.quantity >= 1 && row.valueCents >= 0)
	const hasRows = rows.length >= 1
	const isValid = canSplit && allRowsValid && hasRows && !isOver && !isCurrentEmpty && !isBelowHeld

	const updateRow = (key: string, patch: Partial<Omit<DraftRow, 'key'>>) => {
		setRows((prev) => prev.map((row) => row.key === key ? { ...row, ...patch } : row))
	}

	const handleRowQuantityChange = (key: string, value: number) => {
		updateRow(key, { quantity: clamp(Math.round(value), 1) })
	}

	const handleRowValueChange = (key: string, euros: number) => {
		updateRow(key, { valueCents: eurosToCents(euros) })
	}

	const handleAddRow = () => {
		setRows((prev) => [...prev, {
			key: newRowKey(),
			quantity: 1,
			valueCents: hasAutoBalance ? balanceCents : item.valueCents,
			description: item.description ?? '',
		}])
	}

	const handleRemoveRow = (key: string) => {
		setRows((prev) => prev.filter((row) => row.key !== key))
	}

	const handleConfirm = () => {
		if (!isValid) { return }
		const splits: InventorySplitRequest['splits'] = rows.map((row) => ({
			quantity: row.quantity,
			valueCents: row.valueCents,
			description: row.description.trim() === '' ? null : row.description.trim(),
		}))
		if (hasAutoBalance) {
			splits.push({ quantity: 1, valueCents: balanceCents, description: null })
		}
		onConfirm({ currentQuantity: computedCurrentQuantity, splits })
	}

	const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={isLoading ? () => {} : onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]"
						>
							<div className="px-6 pt-6 pb-4 shrink-0 space-y-4">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{t('inventory.split.title')}
								</DialogTitle>

								<div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-gray-500">{t('inventory.split.quantity')}</span>
										<span className="font-medium text-gray-900">{total}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-500">{t('inventory.split.unitPrice')}</span>
										<span className="font-medium text-gray-900">
											{formatCents(unitPrice)}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-500">{t('inventory.split.totalValue')}</span>
										<span className="font-medium text-gray-900">
											{formatCents(originalTotalValue)}
										</span>
									</div>
									{expiresAt && (
										<div className="flex justify-between">
											<span className="text-gray-500">{t('inventory.split.expiresAt')}</span>
											<span className="font-medium text-gray-900">
												{expiresAt.toLocaleDateString()}
											</span>
										</div>
									)}
								</div>

								<p className="text-sm text-gray-600">
									{t('inventory.split.instructions')}
								</p>
							</div>

							<div className="px-6 py-2 overflow-y-auto flex-1 min-h-0 space-y-4">
								{heldQuantity > 0
									? (
										<div className="bg-brand-50 rounded-md p-3 space-y-2">
											<div className="flex items-center justify-between gap-2">
												<span className="text-sm font-medium text-gray-700">
													{t('inventory.split.heldItem')}
												</span>
												<span className="text-xs font-medium text-brand-700 uppercase tracking-wide">
													{t('inventory.split.locked')}
												</span>
											</div>
											<div className="flex items-end gap-3">
												<div className="w-24 flex-shrink-0">
													<div className="flex flex-col gap-2">
														<span className="text-base text-grey-900">
															{t('inventory.split.columnQuantity')}
														</span>
														<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-white text-grey-600">
															<span className="flex-1 py-2 px-3 tabular-nums text-base">
																{heldQuantity}
															</span>
														</div>
													</div>
												</div>
												<div className="text-gray-400 pb-2">×</div>
												<div className="flex-1 min-w-0 max-w-xs">
													<div className="flex flex-col gap-2">
														<span className="text-base text-grey-900">
															{t('inventory.split.columnUnitPrice')}
														</span>
														<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-white text-grey-600">
															<span
																className="pl-3 pr-2 select-none text-base"
																aria-hidden="true"
															>
																€
															</span>
															<span className="flex-1 py-2 tabular-nums text-base">
																{centsToEuros(unitPrice).toFixed(2)}
															</span>
															<span
																className="px-3 text-xs font-medium select-none whitespace-nowrap"
																aria-hidden="true"
															>
																+ VAT
															</span>
														</div>
													</div>
												</div>
											</div>
										</div>
									)
									: null}

								<div className="bg-gray-50 rounded-md p-3 space-y-2">
									<div className="flex items-center justify-between gap-2">
										<div className="text-sm text-gray-700">
											<span className="font-medium">
												{t('inventory.split.currentItem')}
											</span>
											{item.description && (
												<span className="text-gray-500">· {item.description}</span>
											)}
										</div>
										<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
											{t('inventory.split.autoComputed')}
										</span>
									</div>
									<div className="flex items-end gap-3">
										<div className="w-24 flex-shrink-0">
											<div className="flex flex-col gap-2">
												<span className="text-base text-grey-900">
													{t('inventory.split.columnQuantity')}
												</span>
												<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-grey-100 text-grey-600">
													<span className="flex-1 py-2 px-3 tabular-nums text-base">
														{Math.max(0, computedCurrentQuantity - heldQuantity)}
													</span>
												</div>
											</div>
										</div>
										<div className="text-gray-400 pb-2">×</div>
										<div className="flex-1 min-w-0 max-w-xs">
											<div className="flex flex-col gap-2">
												<span className="text-base text-grey-900">
													{t('inventory.split.columnUnitPrice')}
												</span>
												<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-grey-100 text-grey-600">
													<span
														className="pl-3 pr-2 select-none text-base"
														aria-hidden="true"
													>
														€
													</span>
													<span className="flex-1 py-2 tabular-nums text-base">
														{centsToEuros(unitPrice).toFixed(2)}
													</span>
													<span
														className="px-3 text-xs font-medium select-none whitespace-nowrap"
														aria-hidden="true"
													>
														+ VAT
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								{hasAutoBalance && (
									<div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
										<div className="flex items-center justify-between gap-2">
											<div className="text-sm text-amber-900">
												<span className="font-medium">
													{t('inventory.split.balancingItem')}
												</span>
												{item.description && (
													<span className="text-amber-700">
														· {item.description}
													</span>
												)}
											</div>
											<span className="text-xs font-medium text-amber-700 uppercase tracking-wide">
												{t('inventory.split.autoAdded')}
											</span>
										</div>
										<div className="flex items-end gap-3">
											<div className="w-24 flex-shrink-0">
												<div className="flex flex-col gap-2">
													<span className="text-base text-grey-900">
														{t('inventory.split.columnQuantity')}
													</span>
													<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-grey-100 text-grey-600">
														<span className="flex-1 py-2 px-3 tabular-nums text-base">
															1
														</span>
													</div>
												</div>
											</div>
											<div className="text-gray-400 pb-2">×</div>
											<div className="flex-1 min-w-0 max-w-xs">
												<div className="flex flex-col gap-2">
													<span className="text-base text-grey-900">
														{t('inventory.split.columnUnitPrice')}
													</span>
													<div className="flex items-center rounded-md ring-1 ring-grey-100 bg-grey-100 text-grey-600">
														<span
															className="pl-3 pr-2 select-none text-base"
															aria-hidden="true"
														>
															€
														</span>
														<span className="flex-1 py-2 tabular-nums text-base">
															{centsToEuros(balanceCents).toFixed(2)}
														</span>
														<span
															className="px-3 text-xs font-medium select-none whitespace-nowrap"
															aria-hidden="true"
														>
															+ VAT
														</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}

								{rows.map((row) => (
									<div
										key={row.key}
										className="relative border border-gray-200 rounded-md p-3 space-y-2"
									>
										<IconButton
											variant="ghost"
											size="icon-sm"
											type="button"
											onClick={() => handleRemoveRow(row.key)}
											disabled={isLoading}
											aria-label={t('inventory.split.removeRow')}
											className="absolute top-2 right-2 text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer disabled:cursor-not-allowed"
										>
											<Trash2Icon className="size-4" aria-hidden="true" />
										</IconButton>
										<div className="pr-10">
											<DescriptionCombobox
												label={t('inventory.split.columnDescription')}
												value={row.description}
												onChange={(value) => updateRow(row.key, { description: value })}
												options={catalogueItems.map((catalogueItem) => catalogueItem.name)}
												placeholder={item.description
													?? t('inventory.split.descriptionPlaceholder')}
											/>
										</div>
										<div className="flex items-end gap-3">
											<div className="w-24 flex-shrink-0">
												<AmountField
													label={t('inventory.split.columnQuantity')}
													value={row.quantity}
													onChange={(value) => handleRowQuantityChange(row.key, value)}
													locale={i18n.language}
													integer
													placeholder="1"
												/>
											</div>
											<div className="text-gray-400 pb-2">×</div>
											<div className="flex-1 min-w-0 max-w-xs">
												<AmountField
													label={t('inventory.split.columnUnitPrice')}
													value={centsToEuros(row.valueCents)}
													onChange={(value) => handleRowValueChange(row.key, value)}
													locale={i18n.language}
													prefix="€"
													suffix="+ VAT"
													placeholder="0.00"
												/>
											</div>
										</div>
									</div>
								))}

								<Button
									type="subtle"
									onClick={handleAddRow}
									disabled={isLoading}
								>
									<PlusIcon className="size-4 mr-1" aria-hidden="true" />
									{t('inventory.split.addRow')}
								</Button>
							</div>

							<div className="px-6 pt-4 pb-6 shrink-0 space-y-4">
								<div
									className={`rounded-md px-3 py-2 text-sm flex justify-between items-center ${
										isOver || isCurrentEmpty || isBelowHeld
											? 'bg-red-50 text-red-900'
											: 'bg-green-50 text-green-900'
									}`}
								>
									<span className="font-medium">
										{t('inventory.split.totalValueLabel')}
									</span>
									<span className="tabular-nums">
										{formatCents(originalTotalValue)}
										{isOver && (
											<span className="ml-2 font-medium">
												{t('inventory.split.overBy', {
													amount: formatCents(-remainingValue),
												})}
											</span>
										)}
										{isCurrentEmpty && (
											<span className="ml-2 font-medium">
												{t('inventory.split.currentEmpty')}
											</span>
										)}
										{isBelowHeld && (
											<span className="ml-2 font-medium">
												{t('inventory.split.belowHeld', { held: heldQuantity })}
											</span>
										)}
										{hasAutoBalance && !isOver && !isCurrentEmpty && !isBelowHeld && (
											<span className="ml-2 text-amber-700">
												{t('inventory.split.balanceHint', {
													amount: formatCents(balanceCents),
												})}
											</span>
										)}
									</span>
								</div>

								{error && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
										<div className="font-medium text-red-900">
											{t('inventory.split.errorSplit')}
										</div>
										<div className="text-red-700 mt-0.5">
											{error?.response?.data?.message || error.message || t('common.tryAgain')}
										</div>
									</div>
								)}
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 shrink-0">
								<Button type="subtle" onClick={onClose} disabled={isLoading}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									onClick={handleConfirm}
									isLoading={isLoading}
									disabled={!isValid}
								>
									{isLoading ? t('inventory.split.splitting') : t('inventory.split.confirm')}
								</Button>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
